import type { RefresherEventDetail } from '@ionic/core/components';
import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonGrid,
  IonRow,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSearchbar,
  IonSkeletonText,
} from '@ionic/react';
import { chevronDownCircleOutline, location } from 'ionicons/icons';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import axios from '../../axios';
import Header from '../../components/Header';
import NoRecords from '../../components/NoRecords';
import { AppContext } from '../../providers/AppContext';

const ViewlAllTask: React.FC = () => {
  const [data, setData] = useState<any>({
    dataSet: [],
    loading: false,
  });
  const isMounted = useRef(true);
  const { state } = useContext(AppContext);

  const [isLoading, setIsLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState<string>();

  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState<boolean>(false);

  const getData = (context: any) => {
    isMounted.current = true;
    let url = `method/preflet_asset.apis.task.routes.get_assigned_tasks?limit=${state.maxLength}&start=${context.isFiltered ? 0 : currentPage * 10 + 0
      }`;

    // add search data if any
    if ('search' in context || searchText) {
      setSearchText(context.search || '');
      if (context.search) url += `&search=${context.search || searchText}`;
    }
    if (isMounted.current)
      setIsLoading(true);

    const config: any = {
      method: 'get',
      url,
      headers: {
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };
    axios(config)
      .then((res: any) => {
        if (res?.data?.message && res?.data.message?.get_task_list?.length > 0) {
          if (context.isFiltered || searchText) {
            const rawData = { dataSet: [], loading: false };
            rawData.dataSet = res.data.message.get_task_list;
            if (isMounted.current)
              setData(rawData);
          } else {
            const rawData = { ...data };

            rawData.dataSet.push(...res.data.message.get_task_list);
            if (isMounted.current)
              setData(rawData);
          }
          if (isMounted.current)
            setDisableInfiniteScroll(res.data.message.get_task_list.length < state.maxLength);
        } else {
          const value = { ...data };
          if (searchText) {
            const rawData = { dataSet: [], loading: false };
            rawData.dataSet = res.data.message.get_task_list;
            if (isMounted.current)
              setData(rawData);
          } else {
            if (isMounted.current)
              setData(value);
          }
          if (isMounted.current)
            setDisableInfiniteScroll(true);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        if (isMounted.current) {
          setIsLoading(false);
          setCurrentPage(currentPage + 1);
        }
      });
  };

  useEffect(() => {
    getData({});
    // Cleanup function
    return () => {
      isMounted.current = false; // Mark the component as unmounted when it's being cleaned up
      // You can also cancel any subscriptions or clean up other resources here
    };
  }, []);

  function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    getData({});
    setTimeout(() => {
      event.detail.complete();
    }, 2000);
  }

  async function searchNext($event: CustomEvent<void>) {
    getData({});
    ($event.target as HTMLIonInfiniteScrollElement).complete();
  }

  const { t } = useTranslation();


  const handleSearch = (value: any) => {
    getData({ search: value, isFiltered: true });
  }

  return (
    <IonPage>
      <Header enableBackButton='goBack' title={t('Project.taskList')} />
      <IonSearchbar
        className="data-list"
        value={searchText}

        onIonChange={(e) => handleSearch(e.detail.value!)}
      ></IonSearchbar>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t('Utils.pullRefresh')}
            refreshingSpinner="circles"
            refreshingText={t('Utils.refreshing')}
          ></IonRefresherContent>
        </IonRefresher>

        {isLoading &&
          [1, 2, 3, 4, 5, 6, 7, 8, 9].map((element, key) => {
            return (
              <div key={key} style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <IonSkeletonText animated style={{ width: '60%' }} />
                  <IonSkeletonText animated style={{ width: '20%' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <IonSkeletonText animated style={{ width: '30%' }} />
                  <IonSkeletonText animated style={{ width: '20%' }} />
                </div>
              </div>
            );
          })}
        <IonGrid>
          {data.dataSet.map((item: any, key: any) => {
            return (
              <div
                key={key}
                className="ion-margin data-list"
                style={{ boxShadow: '2px 8px 48px -16px rgba(21, 70, 160, 0.1)', borderRadius: '20px' }}
              >
                <section style={{ display: 'flex' }}>
                  <section style={{ flex: 'column' }}>
                    <h4
                      style={{
                        marginLeft: '0.4rem',
                        marginTop: '1.3rem',
                        color: 'rgba(29, 80, 141, 1)',
                        fontSize: 'large',
                      }}
                    >
                      {item.name}
                    </h4>
                    <p
                      style={{
                        marginLeft: '0.5rem',
                        color: 'rgba(146, 155, 171, 1)',
                        marginTop: '-0.5rem',
                        fontSize: '11px',
                        maxWidth: '200px',
                        wordWrap: 'break-word',
                      }}
                    >
                      {t('Assets.Details.AID')} : {t(`${item.project}`)}
                    </p>
                  </section>
                </section>

                <section style={{ flex: 'column', paddingBottom: '10px' }}>
                  <Link to={{ pathname: '/view-task', state: item.name }}>
                    <p
                      style={{
                        color: 'rgba(29, 80, 141, 1)',
                        float: 'right',
                        fontSize: '13px',
                        flex: 'column',
                        marginTop: '-2.9rem',
                        paddingRight: '1rem',
                        textTransform: 'capitalize',
                        textDecoration: 'underline'
                      }}
                    >
                      {t('Assets.Details.Details')}
                    </p>
                  </Link>
                </section>
              </div>
            );
          })}
          {!filter ? (
            <IonInfiniteScroll onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}>
              <IonInfiniteScrollContent
                loadingSpinner="bubbles"
                loadingText="Loading more data..."
              ></IonInfiniteScrollContent>
            </IonInfiniteScroll>
          ) : (
            ''
          )}
          {!isLoading && data.dataSet.length === 0 ? (
            <IonRow style={{ margin: 'auto', display: 'block' }}>
              <NoRecords width="60%" smallText={false} />
            </IonRow>
          ) : (
            <div></div>
          )}
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default ViewlAllTask;
